import { useMemo } from 'react';

import {
  APP_EVENTS,
  captureAppEvent,
  type TableContext,
} from 'src/services/posthogEvents';

import { Column } from 'src/components/custom_components/CustomTable/CustomTable';

import { getColumnKey } from 'src/models/ColumnConfiguration.model';

const useColumnVisibility = <T>(
  columns: Column<T>[],
  storedKeys: Record<string, boolean> | null | undefined,
  setStoredKeys: (keys: Record<string, boolean>) => void,
  options: {
    defaultVisibility?: Record<string, boolean>;
    tableContext?: TableContext;
  } = {},
) => {
  const { defaultVisibility, tableContext } = options;
  const visibleColumns = useMemo(
    () =>
      storedKeys ??
      Object.fromEntries(
        columns.map((col) => [
          getColumnKey(col),
          defaultVisibility?.[getColumnKey(col)] ?? true,
        ]),
      ),
    [columns, storedKeys, defaultVisibility],
  );

  const handleToggleColumn = (key: string) => {
    const updatedVisibleColumns = {
      ...visibleColumns,
      [key]: !visibleColumns[key],
    };
    setStoredKeys(updatedVisibleColumns);
    if (tableContext) {
      captureAppEvent(APP_EVENTS.TABLE_CONFIG_CHANGED, {
        table: tableContext,
        columns_visible: Object.entries(updatedVisibleColumns)
          .filter(([, visible]) => visible)
          .map(([col]) => col),
      });
    }
  };

  const displayedColumns = useMemo(
    () => columns.filter((col) => visibleColumns[getColumnKey(col)]),
    [columns, visibleColumns],
  );

  return { visibleColumns, handleToggleColumn, displayedColumns };
};

export default useColumnVisibility;
